import { useEffect, useState, FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import BusinessIcon from "@mui/icons-material/Business";
import {
  AuthLookupResult,
  GOOGLE_START_URL,
  SSO_ERROR_MESSAGES,
  getSsoEntry,
  lookupAuth,
  startSso,
} from "../api/sso";
import { asRequestError, requestMessage } from "../utils/requestError";

/**
 * Identifier-first sign-in. People type a username or an email address:
 *  - an email whose domain uses single sign-on gets a "Continue with …"
 *    button (and a quieter "use a password instead" for break-glass);
 *  - anything else gets the password field, as before.
 * "Sign in with Google" is always offered: Google reports the account's
 * domain and the API finds the organisation from it.
 * /login/<slug> skips the lookup and offers that organisation's sign-on.
 */

type Step = "identify" | "password" | "sso";

interface SsoChoice {
  kind: AuthLookupResult["kind"];
  providerName: string;
  startUrl: string;
}

const looksLikeEmail = (value: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());

export default function LoginPage() {
  const { login } = useAuth();
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const ssoError = searchParams.get("sso_error");
  const ssoRef = searchParams.get("ref");

  const [step, setStep] = useState<Step>("identify");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [sso, setSso] = useState<SsoChoice | null>(null);
  const [error, setError] = useState(
    ssoError ? SSO_ERROR_MESSAGES[ssoError] ?? "Single sign-on didn't complete. Try again." : ""
  );
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  // /login/<slug>: offer that organisation's single sign-on straight away.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getSsoEntry(slug).then((entry) => {
      if (cancelled) return;
      if (entry) {
        setSso({ kind: entry.kind, providerName: entry.provider_name, startUrl: entry.start_url });
        setStep("sso");
      } else if (!ssoError) {
        setNotice("Single sign-on isn't set up for this organisation. Sign in below.");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug, ssoError]);

  const handleContinue = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setNotice("");
    const value = identifier.trim();
    if (!value) return;
    if (!looksLikeEmail(value)) {
      setStep("password");
      return;
    }
    setLoading(true);
    try {
      const result = await lookupAuth(value);
      if (result.mode === "sso" && result.start_url && result.provider_name) {
        setSso({ kind: result.kind, providerName: result.provider_name, startUrl: result.start_url });
        setStep("sso");
      } else {
        if (result.ambiguous) {
          setNotice(
            "More than one organisation uses this email domain. Use your organisation's sign-in link, or sign in with a password."
          );
        }
        setStep("password");
      }
    } catch {
      // Lookup is a convenience; never block a password login on it.
      setStep("password");
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
    } catch (err: unknown) {
      const e = asRequestError(err);
      const fields = e.data?.fields as
        | { code?: string; provider_name?: string; start_url?: string }
        | undefined;
      if (e.status === 403 && fields?.code === "sso_required" && fields.start_url) {
        setSso({ kind: null, providerName: fields.provider_name ?? "your organisation", startUrl: fields.start_url });
        setStep("sso");
        setPassword("");
        setNotice("Your organisation requires single sign-on.");
      } else {
        setError(requestMessage(err, "Sign-in failed. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const continueWithSso = () => {
    if (!sso) return;
    setLoading(true);
    startSso(sso.startUrl, {
      email: looksLikeEmail(identifier) ? identifier.trim() : undefined,
      next,
    });
  };

  const backToStart = () => {
    setStep("identify");
    setPassword("");
    setError("");
    setNotice("");
  };

  const signInWithGoogle = () => {
    setLoading(true);
    startSso(GOOGLE_START_URL, {
      email: looksLikeEmail(identifier) ? identifier.trim() : undefined,
      next,
    });
  };

  const googleButton = (
    <>
      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">
          or
        </Typography>
      </Divider>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        startIcon={<GoogleIcon />}
        onClick={signInWithGoogle}
        disabled={loading}
      >
        Sign in with Google
      </Button>
    </>
  );

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Sign in
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              {ssoRef && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
                  Reference {ssoRef} — quote it if you contact support.
                </Typography>
              )}
            </Alert>
          )}
          {notice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {notice}
            </Alert>
          )}

          {step === "identify" && (
            <Box component="form" onSubmit={handleContinue} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="identifier"
                label="Username or email"
                name="username"
                autoComplete="username"
                autoFocus
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                sx={{ mt: 3, mb: 1 }}
                disabled={loading || !identifier.trim()}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Continue"}
              </Button>
              {googleButton}
            </Box>
          )}

          {step === "password" && (
            <Box component="form" onSubmit={handlePassword} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username or email"
                name="username"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                sx={{ mt: 3, mb: 1 }}
                disabled={loading || !identifier.trim() || !password}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Sign in"}
              </Button>
              <Link component="button" type="button" variant="body2" onClick={backToStart}>
                Use a different account
              </Link>
              {googleButton}
            </Box>
          )}

          {step === "sso" && sso && (
            <Box>
              {identifier && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {identifier}
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={sso.kind === "google_shared" ? <GoogleIcon /> : <BusinessIcon />}
                onClick={continueWithSso}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : sso.kind === "google_shared" ? (
                  "Sign in with Google"
                ) : (
                  `Continue with ${sso.providerName}`
                )}
              </Button>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setNotice("");
                    setStep("password");
                  }}
                >
                  Use a password instead
                </Link>
                {!slug && (
                  <Link component="button" type="button" variant="body2" onClick={backToStart}>
                    Use a different account
                  </Link>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
